// src/pages/orders/components/OrderDetailsModal.jsx
import React, { useState } from 'react';
import {
  X,
  Package,
  User,
  MapPin,
  Phone,
  Mail,
  Calendar,
  DollarSign,
  Store,
  Truck,
  FileText,
  Paperclip,
  Eye,
  Edit,
  ExternalLink,
  AlertCircle,
  Save,
  XCircle as CancelIcon,
  Edit2
} from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAlert } from '../../../contexts/AlertContext';
import { updateEcomOrder } from '../../../services/api/orders';
import { formatCurrency } from '../../../utils/orderCalculations';
import { formatDate, formatDateTime } from '../../../utils/dateUtils';
import { getImageUrl } from '../../../utils/imageUtils';
import { PaymentMethodIcon } from '../../../utils/paymentMethodIcons';
import styles from './OrderDetailsModal.module.css';
import BundleItemGroup from '../../../components/orders/BundleItemGroup/BundleItemGroup';
import OrderShiftLink from '../../../components/orders/OrderShiftLink/OrderShiftLink';
import OrderOriginBadge from '../../../components/orders/OrderOriginBadge/OrderOriginBadge';
import { groupOrderItems } from '../../../utils/orderItemGrouping';

const isPdfUrl = (url) => !!url && url.toLowerCase().endsWith('.pdf');

const OrderDetailsModal = ({ order, onClose, onUpdate }) => {
  const { t } = useLocalization();
  const { hasPermission } = usePermissions();
  const { success, error: showError } = useAlert();

  const [isUpdating, setIsUpdating] = useState(false);
  const [localOrder, setLocalOrder] = useState(order);
  
  // Edit states for notes
  const [isEditingDeliveryNotes, setIsEditingDeliveryNotes] = useState(false);
  const [isEditingSpecialNotes, setIsEditingSpecialNotes] = useState(false);
  const [deliveryNotesValue, setDeliveryNotesValue] = useState(order.deliveryNotes || '');
  const [specialNotesValue, setSpecialNotesValue] = useState(order.specialNotes || '');

  const canEdit = hasPermission('edit_ecom_order');
  const isDispatched = localOrder.OrderStatus?.isDispatched;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleViewOrder = () => {
    window.open(`/orders/${order.id}`, '_blank');
  };

  const handleEditOrder = () => {
    window.open(`/orders/${order.id}/edit`, '_blank');
  };

  const canEditOrder = () => {
    if (localOrder.OrderStatus?.isInPacking && !hasPermission('edit_in_packing_order')) {
      return false;
    }
    
    if (localOrder.OrderStatus?.isDispatched && !hasPermission('edit_dispatched_order')) {
      return false;
    }
    
    return hasPermission('edit_ecom_order');
  };

  const handleToggleUrgent = async () => {
    if (!canEdit || isDispatched) return;

    setIsUpdating(true);
    try {
      const response = await updateEcomOrder(localOrder.id, {
        isUrgent: !localOrder.isUrgent
      });

      const updatedOrder = { ...localOrder, isUrgent: !localOrder.isUrgent };
      setLocalOrder(updatedOrder);
      onUpdate(updatedOrder);
      
      success(
        localOrder.isUrgent 
          ? 'Order unmarked as urgent' 
          : 'Order marked as urgent'
      );
    } catch (error) {
      console.error('Failed to update urgent status:', error);
      showError('Failed to update urgent status');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSaveDeliveryNotes = async () => {
    setIsUpdating(true);
    try {
      await updateEcomOrder(localOrder.id, {
        deliveryNotes: deliveryNotesValue
      });

      const updatedOrder = { ...localOrder, deliveryNotes: deliveryNotesValue };
      setLocalOrder(updatedOrder);
      onUpdate(updatedOrder);
      setIsEditingDeliveryNotes(false);
      
      success('Delivery notes updated successfully');
    } catch (error) {
      console.error('Failed to update delivery notes:', error);
      showError('Failed to update delivery notes');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSaveSpecialNotes = async () => {
    setIsUpdating(true);
    try {
      await updateEcomOrder(localOrder.id, {
        specialNotes: specialNotesValue
      });

      const updatedOrder = { ...localOrder, specialNotes: specialNotesValue };
      setLocalOrder(updatedOrder);
      onUpdate(updatedOrder);
      setIsEditingSpecialNotes(false);
      
      success('Special notes updated successfully');
    } catch (error) {
      console.error('Failed to update special notes:', error);
      showError('Failed to update special notes');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelDeliveryNotes = () => {
    setDeliveryNotesValue(localOrder.deliveryNotes || '');
    setIsEditingDeliveryNotes(false);
  };

  const handleCancelSpecialNotes = () => {
    setSpecialNotesValue(localOrder.specialNotes || '');
    setIsEditingSpecialNotes(false);
  };

  const primaryPhone = localOrder.phoneNumbers?.find(p => p.isPrimary) || localOrder.phoneNumbers?.[0];

  const renderItemRow = (item, key, rowClass) => (
    <tr key={key} className={rowClass}>
      <td>
        <div className={styles.productInfo}>
          <span className={styles.productName}>{item.Product?.name || 'N/A'}</span>
          {item.Product?.sku && (
            <span className={styles.productSku}>SKU: {item.Product.sku}</span>
          )}
        </div>
      </td>
      <td className={styles.textRight}>{item.quantity}</td>
      <td className={styles.textRight}>{formatCurrency(item.unitPrice)}</td>
      <td className={styles.textRight}>
        {item.lineDiscount > 0 ? formatCurrency(item.lineDiscount) : '-'}
      </td>
      <td className={styles.textRight}>
        <strong>{formatCurrency(item.lineTotal)}</strong>
      </td>
    </tr>
  );

  return (
    <div className={styles.modalOverlay} onClick={handleBackdropClick}>
      <div className={styles.modalContent}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerTitle}>
              <Package size={24} />
              <div>
                <div className={styles.headerTitleRow}>
                  <h2>{t('ecomOrders.orderDetails')}</h2>
                  {localOrder.isUrgent && (
                    <span className={styles.urgentBadge}>
                      <AlertCircle size={14} />
                      Urgent
                    </span>
                  )}
                </div>
                <p className={styles.orderNumber}>#{localOrder.orderNumber}</p>
              </div>
            </div>
            <button onClick={onClose} className={styles.closeButton}>
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className={styles.modalBody}>
          {/* Customer Information */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>
                <User size={18} />
                {t('ecomOrders.customerInformation')}
              </h3>
              {canEdit && !isDispatched && (
                <button
                  className={`${styles.urgentToggle} ${localOrder.isUrgent ? styles.active : ''}`}
                  onClick={handleToggleUrgent}
                  disabled={isUpdating}
                >
                  <AlertCircle size={16} />
                  {localOrder.isUrgent ? 'Unmark Urgent' : 'Mark as Urgent'}
                </button>
              )}
            </div>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <label>{t('ecomOrders.customerName')}</label>
                <p>{localOrder.customerName}</p>
              </div>
              {localOrder.customerEmail && (
                <div className={styles.infoItem}>
                  <label>{t('ecomOrders.customerEmail')}</label>
                  <p className={styles.emailLink}>
                    <Mail size={14} />
                    {localOrder.customerEmail}
                  </p>
                </div>
              )}
              <div className={styles.infoItem}>
                <label>{t('ecomOrders.phoneNumbers')}</label>
                <div className={styles.phoneList}>
                  {localOrder.phoneNumbers?.map((phone, idx) => (
                    <a 
                      key={idx}
                      href={`tel:${phone.phoneNumber}`}
                      className={`${styles.phoneLink} ${phone.isPrimary ? styles.primaryPhone : ''}`}
                    >
                      <Phone size={14} />
                      {phone.phoneNumber}
                      {phone.isPrimary && <span className={styles.primaryBadge}>Primary</span>}
                    </a>
                  ))}
                </div>
              </div>
              <div className={styles.infoItem}>
                <label>{t('ecomOrders.address')}</label>
                <p className={styles.addressText}>
                  <MapPin size={14} />
                  {localOrder.address}
                </p>
              </div>
              <div className={styles.infoItem}>
                <label>{t('ecomOrders.city')}</label>
                <p>{localOrder.City?.name || 'N/A'}</p>
              </div>
              
              {/* Delivery Notes - Editable */}
              <div className={styles.infoItem}>
                <div className={styles.infoItemHeader}>
                  <label>{t('ecomOrders.deliveryNotes')}</label>
                  {canEdit && !isDispatched && !isEditingDeliveryNotes && (
                    <button
                      className={styles.editIconButton}
                      onClick={() => setIsEditingDeliveryNotes(true)}
                      title="Edit delivery notes"
                    >
                      <Edit2 size={14} />
                    </button>
                  )}
                </div>
                {isEditingDeliveryNotes ? (
                  <div className={styles.editField}>
                    <textarea
                      value={deliveryNotesValue}
                      onChange={(e) => setDeliveryNotesValue(e.target.value)}
                      className={styles.textarea}
                      rows={3}
                      placeholder="Enter delivery notes..."
                      disabled={isUpdating}
                    />
                    <div className={styles.editActions}>
                      <button
                        className={styles.saveButton}
                        onClick={handleSaveDeliveryNotes}
                        disabled={isUpdating}
                      >
                        <Save size={14} />
                        Save
                      </button>
                      <button
                        className={styles.cancelEditButton}
                        onClick={handleCancelDeliveryNotes}
                        disabled={isUpdating}
                      >
                        <CancelIcon size={14} />
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className={styles.notesText}>
                    {localOrder.deliveryNotes || 'No delivery notes'}
                  </p>
                )}
              </div>

              {/* Special Notes - Editable */}
              <div className={styles.infoItem}>
                <div className={styles.infoItemHeader}>
                  <label>{t('ecomOrders.specialNotes')}</label>
                  {canEdit && !isDispatched && !isEditingSpecialNotes && (
                    <button
                      className={styles.editIconButton}
                      onClick={() => setIsEditingSpecialNotes(true)}
                      title="Edit special notes"
                    >
                      <Edit2 size={14} />
                    </button>
                  )}
                </div>
                {isEditingSpecialNotes ? (
                  <div className={styles.editField}>
                    <textarea
                      value={specialNotesValue}
                      onChange={(e) => setSpecialNotesValue(e.target.value)}
                      className={styles.textarea}
                      rows={3}
                      placeholder="Enter special notes..."
                      disabled={isUpdating}
                    />
                    <div className={styles.editActions}>
                      <button
                        className={styles.saveButton}
                        onClick={handleSaveSpecialNotes}
                        disabled={isUpdating}
                      >
                        <Save size={14} />
                        Save
                      </button>
                      <button
                        className={styles.cancelEditButton}
                        onClick={handleCancelSpecialNotes}
                        disabled={isUpdating}
                      >
                        <CancelIcon size={14} />
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className={styles.notesText}>
                    {localOrder.specialNotes || 'No special notes'}
                  </p>
                )}
              </div>

              <div className={styles.infoItem}>
                <label>{t('ecomOrders.shopName')}</label>
                <p>{localOrder.Shop?.name || 'N/A'}</p>
              </div>
              {localOrder.shiftMap?.shift && (
                <div className={styles.infoItem}>
                  <label>{t('ecomOrders.messageCenterShift')}</label>
                  <p><OrderShiftLink shiftMap={localOrder.shiftMap} /></p>
                </div>
              )}
              {localOrder.OrderOrigin && (
                <div className={styles.infoItem}>
                  <label>{t('ecomOrders.orderOrigin')}</label>
                  <OrderOriginBadge origin={localOrder.OrderOrigin} />
                </div>
              )}
              {localOrder.privateReference && (
                <div className={styles.infoItem}>
                  <label>{t('ecomOrders.privateReference')}</label>
                  <p>{localOrder.privateReference}</p>
                </div>
              )}
              <div className={styles.infoItem}>
                <label>{t('ecomOrders.paymentType')}</label>
                <p>{localOrder.PaymentType?.label || localOrder.paymentTypeKey}</p>
                {localOrder.paymentRecords?.length > 0 && (
                  <div className={styles.paymentRecordsList}>
                    {localOrder.paymentRecords.map((pr, idx) => (
                      <div key={idx} className={styles.paymentRecordItem}>
                        <PaymentMethodIcon
                          iconFileName={pr.PaymentMethod?.iconFileName}
                          label={pr.PaymentMethod?.label}
                          className={styles.paymentRecordMethod}
                          verified={pr.isVerified}
                        />
                        {pr.reference && (
                          <span className={styles.paymentRecordRef}>#{pr.reference}</span>
                        )}
                        <span className={styles.paymentRecordAmount}>{pr.paymentAmount}</span>
                        {pr.proofUrl && (
                          isPdfUrl(pr.proofUrl) ? (
                            <a href={getImageUrl(pr.proofUrl)} target="_blank" rel="noopener noreferrer" className={styles.proofFileLink}>
                              <Paperclip size={14} />
                              {t('ecomOrders.viewProofPdf') || 'View uploaded PDF'}
                            </a>
                          ) : (
                            <a href={getImageUrl(pr.proofUrl)} target="_blank" rel="noopener noreferrer" className={styles.proofThumbnailLink}>
                              <img src={getImageUrl(pr.proofUrl)} alt="Payment proof" className={styles.proofThumbnail} />
                            </a>
                          )
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className={styles.infoItem}>
                <label>{t('ecomOrders.status')}</label>
                <div 
                  className={styles.statusBadge}
                  style={{
                    backgroundColor: localOrder.OrderStatus?.colorCode 
                      ? `${localOrder.OrderStatus.colorCode}20` 
                      : '#F59E0B20',
                    borderColor: localOrder.OrderStatus?.colorCode || '#F59E0B',
                    color: localOrder.OrderStatus?.colorCode || '#92400e'
                  }}
                >
                  {localOrder.OrderStatus?.label || localOrder.orderStatusKey}
                </div>
              </div>
              {localOrder.OrderStatusReason && (
                <div className={styles.infoItem}>
                  <label>{t('ecomOrders.statusReason')}</label>
                  <p>{localOrder.OrderStatusReason.label}</p>
                </div>
              )}
              {localOrder.holdUntilDate && (
                <div className={styles.infoItem}>
                  <label>{t('ecomOrders.holdUntilDate')}</label>
                  <p>{formatDate(localOrder.holdUntilDate)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Order Summary and Courier Information */}
          <div className={styles.twoColumnSection}>
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>
                <DollarSign size={18} />
                {t('ecomOrders.orderSummary')}
              </h3>
              <div className={styles.summaryGrid}>
                <div className={styles.summaryRow}>
                  <span>{t('ecomOrders.subtotal')}</span>
                  <span>{formatCurrency(localOrder.totalAmount)}</span>
                </div>
                {localOrder.totalDiscount > 0 && (
                  <div className={styles.summaryRow}>
                    <span>{t('ecomOrders.totalDiscount')}</span>
                    <span className={styles.discountAmount}>-{formatCurrency(localOrder.totalDiscount)}</span>
                  </div>
                )}
                <div className={styles.summaryRow}>
                  <span>{t('ecomOrders.deliveryCharge')}</span>
                  <span>{formatCurrency(localOrder.deliveryCharge)}</span>
                </div>
                {localOrder.tax > 0 && (
                  <div className={styles.summaryRow}>
                    <span>{t('ecomOrders.tax')}</span>
                    <span>{formatCurrency(localOrder.tax)}</span>
                  </div>
                )}
                <div className={`${styles.summaryRow} ${styles.grandTotalRow}`}>
                  <span>{t('ecomOrders.grandTotal')}</span>
                  <span>{formatCurrency(localOrder.grandTotal)}</span>
                </div>
              </div>
            </div>

            {(localOrder.airwaybillNumber || localOrder.BusinessCourier || localOrder.courierStatus) && (
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  <Truck size={18} />
                  {t('ecomOrders.courierInformation')}
                </h3>
                <div className={styles.infoGrid}>
                  {localOrder.airwaybillNumber && (
                    <div className={styles.infoItem}>
                      <label>{t('ecomOrders.airwaybillNumber')}</label>
                      <p className={styles.airwaybillText}>{localOrder.airwaybillNumber}</p>
                    </div>
                  )}
                  {localOrder.BusinessCourier && (
                    <div className={styles.infoItem}>
                      <label>{t('ecomOrders.courierName')}</label>
                      <p>{localOrder.BusinessCourier.name}</p>
                    </div>
                  )}
                  {localOrder.courierStatus && (
                    <div className={styles.infoItem}>
                      <label>{t('ecomOrders.courierStatus')}</label>
                      <div className={styles.courierStatusBadge}>
                        <Truck size={13} />
                        {localOrder.courierStatus.statusLabel}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Order Items */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <Package size={18} />
              {t('ecomOrders.orderItems')}
            </h3>
            <div className={styles.itemsTable}>
              <table>
                <thead>
                  <tr>
                    <th>{t('ecomOrders.product')}</th>
                    <th className={styles.textRight}>{t('ecomOrders.quantity')}</th>
                    <th className={styles.textRight}>{t('ecomOrders.unitPrice')}</th>
                    <th className={styles.textRight}>{t('ecomOrders.lineDiscount')}</th>
                    <th className={styles.textRight}>{t('ecomOrders.lineTotal')}</th>
                  </tr>
                </thead>
                <tbody>
                  {groupOrderItems(localOrder.orderItems || []).map((entry, gIdx) => (entry.type === 'bundle' ? (
                    <BundleItemGroup
                      key={entry.groupKey}
                      group={entry}
                      renderItem={(item, idx, rowClass) => renderItemRow(item, `${entry.groupKey}-${idx}`, rowClass)}
                    />
                  ) : renderItemRow(entry.item, gIdx)))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Additional Information */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <FileText size={18} />
              {t('ecomOrders.additionalInformation')}
            </h3>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <label>{t('ecomOrders.createdBy')}</label>
                <p>{localOrder.createdBy?.name || 'N/A'}</p>
              </div>
              <div className={styles.infoItem}>
                <label>{t('ecomOrders.createdAt')}</label>
                <p>
                  <Calendar size={14} />
                  {formatDateTime(localOrder.createdAt)}
                </p>
              </div>
              {localOrder.confirmedByUser && (
                <div className={styles.infoItem}>
                  <label>{t('ecomOrders.confirmedBy')}</label>
                  <p>{localOrder.confirmedByUser.name}</p>
                </div>
              )}
              {localOrder.confirmedAt && (
                <div className={styles.infoItem}>
                  <label>{t('ecomOrders.confirmedAt')}</label>
                  <p>
                    <Calendar size={14} />
                    {formatDateTime(localOrder.confirmedAt)}
                  </p>
                </div>
              )}
              {localOrder.updatedAt && localOrder.updatedAt !== localOrder.createdAt && (
                <div className={styles.infoItem}>
                  <label>{t('ecomOrders.updatedAt')}</label>
                  <p>
                    <Calendar size={14} />
                    {formatDateTime(localOrder.updatedAt)}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={styles.modalFooter}>
          <button onClick={onClose} className={styles.cancelButton}>
            {t('common.close')}
          </button>
          <div className={styles.actionButtons}>
            {hasPermission('view_ecom_order') && (
              <button onClick={handleViewOrder} className={styles.viewButton}>
                <Eye size={16} />
                {t('common.view')}
                <ExternalLink size={14} />
              </button>
            )}
            {canEditOrder() && (
              <button onClick={handleEditOrder} className={styles.editButton}>
                <Edit size={16} />
                {t('common.edit')}
                <ExternalLink size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsModal;